import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Package, Clock, Truck, CheckCircle2, XCircle, Upload, MessageCircle, ChevronRight } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError } from "@/lib/api";
import TrackingPanel from "@/components/TrackingPanel";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_META = {
  pending_payment: { label: "Menunggu Pembayaran", color: "bg-[#FFB703]/15 text-[#B5790F]", icon: Clock },
  paid: { label: "Pembayaran Diverifikasi", color: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  processing: { label: "Diproses", color: "bg-purple-100 text-purple-700", icon: Package },
  shipped: { label: "Dikirim", color: "brand-soft-bg brand-text-primary", icon: Truck },
  completed: { label: "Selesai", color: "bg-[#2D6A4F]/15 text-[#2D6A4F]", icon: CheckCircle2 },
  cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-700", icon: XCircle },
};

export function OrdersListPage() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/pesanan"); return; }
    const params = tab === "all" ? {} : { status: tab };
    api.get("/orders", { params }).then((r) => setOrders(r.data));
  }, [user, tab, navigate, authLoading]);

  if (authLoading) return <div className="p-12 text-center text-[#5C5C5C]">Memuat...</div>;
  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-4">Pesanan Saya</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5E5E5] rounded-xl p-1 w-full overflow-x-auto no-scrollbar justify-start">
          {[
            { v: "all", l: "Semua" },
            { v: "pending_payment", l: "Menunggu Bayar" },
            { v: "paid", l: "Diverifikasi" },
            { v: "processing", l: "Diproses" },
            { v: "shipped", l: "Dikirim" },
            { v: "completed", l: "Selesai" },
            { v: "cancelled", l: "Batal" },
          ].map((t) => (
            <TabsTrigger key={t.v} value={t.v} className="data-[state=active]:brand-active-menu rounded-lg whitespace-nowrap" data-testid={`orders-tab-${t.v}`}>
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-4 space-y-3">
        {orders.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center">
            <Package className="mx-auto text-[#E5E5E5]" size={40} />
            <div className="mt-3 text-[#5C5C5C]">Belum ada pesanan</div>
            <Link to="/katalog"><Button className="mt-4 brand-btn-primary rounded-xl">Mulai Belanja</Button></Link>
          </div>
        ) : orders.map((o) => {
          const meta = STATUS_META[o.status] || STATUS_META.pending_payment;
          return (
            <Link key={o.id} to={`/pesanan/${o.id}`} data-testid={`order-card-${o.id}`} className="block bg-white border border-[#E5E5E5] rounded-2xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-xs text-[#5C5C5C]">#{o.order_number}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${meta.color}`}>{meta.label}</span>
              </div>
              <div className="flex gap-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#FAF9F6] shrink-0">
                  {o.items?.[0]?.image && <img src={imageUrl(o.items[0].image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm line-clamp-1">{o.items?.[0]?.name}</div>
                  <div className="text-xs text-[#5C5C5C]">+{o.items.length - 1 > 0 ? `${o.items.length - 1} produk lain` : `${o.items.length} produk`}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#5C5C5C]">Total</div>
                  <div className="font-display font-black brand-text-primary">{formatRp(o.total)}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tracking, setTracking] = useState(null);
  const navigate = useNavigate();

  // Midtrans redirect banner: ?payment=success|pending|failed (di-set oleh callbacks Midtrans Snap).
  const paymentParam = searchParams.get("payment");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/orders/${orderId}`);
      setOrder(r.data);
      return r.data;
    } catch {
      setOrder(null);
      return null;
    }
  }, [orderId]);

  const loadTracking = useCallback(async () => {
    try {
      const r = await api.get(`/orders/${orderId}/tracking`);
      setTracking(r.data);
    } catch { /* silent — tracking optional */ }
  }, [orderId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    loadTracking();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/payment-methods").then((r) => setPaymentInfo(r.data)).catch(() => {});
  }, [user, authLoading, navigate, load, loadTracking]);

  // Snap.js loader saat order ini bayar via Midtrans dan masih pending_payment.
  useEffect(() => {
    if (!order || order.payment_method !== "midtrans") return undefined;
    if (order.status !== "pending_payment") return undefined;
    const mt = paymentInfo?.midtrans;
    if (!mt?.available || !mt?.client_key) return undefined;
    const snapSrc = mt.sandbox
      ? "https://app.sandbox.midtrans.com/snap/snap.js"
      : "https://app.midtrans.com/snap/snap.js";
    if (document.querySelector(`script[src="${snapSrc}"]`)) return undefined;
    const script = document.createElement("script");
    script.src = snapSrc;
    script.setAttribute("data-client-key", mt.client_key);
    script.async = true;
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch (_) { /* ignore */ } };
  }, [order, paymentInfo]);

  // Polling helper — pull dari Midtrans Status API sampai payment_status=paid atau
  // timeout. Extracted supaya bisa di-trigger dari (a) URL param ?payment=success
  // (return dari Simulator) dan (b) Snap popup onSuccess (in-page).
  const startMidtransPolling = React.useCallback(() => {
    if (!order) return;
    if (order.payment_status === "paid") return;
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (attempts >= 20) {
        toast.warning(
          "Pembayaran berhasil, tetapi status masih diproses. Silakan refresh beberapa saat lagi.",
          { id: "midtrans-poll-timeout", duration: 8000 },
        );
        return;
      }
      attempts += 1;
      try { await api.post(`/orders/${order.id}/payment/midtrans/check`, {}); } catch { /* silent */ }
      const fresh = await load();
      if (fresh && fresh.payment_status === "paid") {
        cancelled = true;
        toast.success("Pembayaran diterima. Pesanan sedang diproses.", { id: "midtrans-paid-toast" });
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [order, load]);

  // Saat redirect dari Midtrans (?payment=success), aktifkan polling.
  // Backward-compat dengan alur redirect-based (bukan Snap popup).
  useEffect(() => {
    if (paymentParam !== "success") return undefined;
    return startMidtransPolling();
  }, [paymentParam, startMidtransPolling]);

  if (authLoading || !order) return <div className="p-12 text-center text-[#5C5C5C]">Memuat...</div>;
  const meta = STATUS_META[order.status] || STATUS_META.pending_payment;
  const Icon = meta.icon;

  const uploadProof = async () => {
    if (!proofFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      await api.post(`/orders/${order.id}/payment-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti transfer terupload");
      setProofFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  const cancelOrder = async () => {
    if (!confirm("Batalkan pesanan ini?")) return;
    try { await api.post(`/orders/${order.id}/cancel`); toast.success("Pesanan dibatalkan"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeOrder = async () => {
    try { await api.post(`/orders/${order.id}/complete`); toast.success("Pesanan ditandai selesai"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const waPhone = settings?.whatsapp_number;
  const labelMap = { bca: "Transfer BCA", mandiri: "Transfer Mandiri", bni: "Transfer BNI", bri: "Transfer BRI", qris: "QRIS", midtrans: "Pembayaran Online (Midtrans)" };
  const bankAccount = settings?.bank_accounts?.find((b) => b.bank?.toLowerCase() === order.payment_method);
  const isMidtrans = order.payment_method === "midtrans";

  const payWithMidtrans = async () => {
    try {
      const r = await api.post(`/orders/${order.id}/payment/midtrans/snap`, {});
      const token = r?.data?.snap_token;
      if (!token) throw new Error("Token Snap kosong");
      if (typeof window === "undefined" || !window.snap) {
        toast.error("Snap.js belum siap. Refresh halaman dan coba lagi.");
        return;
      }
      window.snap.pay(token, {
        onSuccess: () => {
          toast.success("Pembayaran berhasil! Sinkronisasi status…", { id: "midtrans-snap-success" });
          load();
          startMidtransPolling();  // HOTFIX: trigger polling untuk menunggu webhook sync
        },
        onPending: () => {
          toast.info("Menunggu pembayaran Anda.");
          load();
          startMidtransPolling();  // Poll juga untuk pending (webhook mungkin sudah "settlement")
        },
        onError: () => { toast.error("Pembayaran gagal. Silakan coba lagi."); },
        onClose: () => { toast.warning("Popup pembayaran ditutup."); load(); },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <Link to="/pesanan" className="text-sm text-[#5C5C5C] hover:brand-text-primary mb-3 inline-block">← Kembali ke daftar pesanan</Link>

      {/* Banner Midtrans Redirect — muncul saat customer kembali dari Snap popup (?payment=...) */}
      {paymentParam === "success" && (
        <div className="bg-emerald-50 border-2 border-emerald-500 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="midtrans-banner-success">
          <CheckCircle2 className="text-emerald-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-800">Pembayaran berhasil.</div>
            <div className="text-sm text-emerald-700 mt-0.5">Status pesanan sedang diperbarui. Mohon tunggu sebentar.</div>
          </div>
          <button onClick={() => setSearchParams({})} className="text-emerald-700 hover:text-emerald-900" aria-label="Tutup">×</button>
        </div>
      )}
      {paymentParam === "pending" && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="midtrans-banner-pending">
          <Clock className="text-amber-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-display font-bold text-amber-800">Pembayaran sedang menunggu penyelesaian.</div>
            <div className="text-sm text-amber-700 mt-0.5">Selesaikan pembayaran sesuai instruksi yang Anda pilih.</div>
          </div>
          <button onClick={() => setSearchParams({})} className="text-amber-700 hover:text-amber-900" aria-label="Tutup">×</button>
        </div>
      )}
      {paymentParam === "failed" && (
        <div className="bg-red-50 border-2 border-red-500 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="midtrans-banner-failed">
          <XCircle className="text-red-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-display font-bold text-red-800">Pembayaran gagal.</div>
            <div className="text-sm text-red-700 mt-0.5">Silakan coba lagi atau pilih metode pembayaran lain.</div>
          </div>
          <button onClick={() => setSearchParams({})} className="text-red-700 hover:text-red-900" aria-label="Tutup">×</button>
        </div>
      )}

      <div className={`rounded-2xl p-5 mb-4 flex items-center gap-4 ${meta.color}`}>
        <Icon size={32} />
        <div>
          <div className="text-xs uppercase font-bold tracking-widest opacity-80">Status Pesanan</div>
          <div className="font-display text-xl font-black">{meta.label}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs opacity-80">No Pesanan</div>
          <div className="font-bold text-sm">{order.order_number}</div>
        </div>
      </div>

      {/* Banner sukses persisten saat paid via Midtrans */}
      {isMidtrans && order.payment_status === "paid" && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="midtrans-paid-info">
          <CheckCircle2 className="text-emerald-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-800">✅ Pembayaran diterima.</div>
            <div className="text-sm text-emerald-700 mt-0.5">Pesanan Anda sedang diproses oleh tim kami.</div>
          </div>
        </div>
      )}

      {/* Payment instructions — hanya tampil saat benar-benar perlu bayar.
          Untuk Midtrans: gate ekstra `payment_status != paid` supaya tidak muncul
          saat webhook/Status API sudah mengubahnya ke paid. */}
      {order.status === "pending_payment" && !(isMidtrans && order.payment_status === "paid") && (
        <section className="bg-white border-2 brand-border-primary rounded-2xl p-5 mb-4">
          <h3 className="font-display font-bold mb-2">Instruksi Pembayaran — {labelMap[order.payment_method] || order.payment_method}</h3>

          {isMidtrans ? (
            <div className="space-y-4" data-testid="midtrans-pay-section">
              <div className="brand-soft-bg rounded-xl p-4">
                <div className="text-xs uppercase tracking-widest font-bold brand-text-primary mb-1">Bayar Online</div>
                <div className="text-sm text-[#5C5C5C]">
                  Klik tombol di bawah untuk membuka popup pembayaran Midtrans. Pilih metode: kartu kredit, GoPay, OVO, ShopeePay, Virtual Account, dan lainnya.
                </div>
                {paymentInfo?.midtrans?.sandbox && (
                  <div className="mt-2 text-[10px] brand-text-primary font-bold uppercase tracking-wider">Mode Sandbox — pembayaran simulasi</div>
                )}
              </div>
              <div className="text-2xl font-display font-black brand-text-primary">{formatRp(order.total)}</div>
              <Button
                onClick={payWithMidtrans}
                disabled={!paymentInfo?.midtrans?.available}
                className="brand-btn-primary rounded-xl h-12 px-6 font-bold"
                data-testid="midtrans-pay-now-btn"
              >
                Bayar Sekarang
              </Button>
              {!paymentInfo?.midtrans?.available && (
                <div className="text-xs brand-text-primary bg-red-50 rounded-xl p-2">
                  Midtrans saat ini tidak tersedia. Hubungi admin untuk metode pembayaran alternatif.
                </div>
              )}
              <Button variant="outline" onClick={cancelOrder} className="rounded-xl brand-text-primary brand-border-primary" data-testid="cancel-order-btn">Batalkan Pesanan</Button>
            </div>
          ) : (
            <>
              {order.payment_rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3" data-testid="payment-rejection-notice">
                  <div className="text-xs uppercase tracking-widest text-red-700 font-bold">⚠ Pembayaran Sebelumnya Ditolak</div>
                  <div className="text-sm text-red-700 mt-1">{order.payment_rejection_reason}</div>
                  <div className="text-xs text-red-600 mt-1">Silakan upload ulang bukti pembayaran yang valid.</div>
                </div>
              )}

              {order.payment_method === "qris" ? (
            settings?.qris_image ? (
              <div className="bg-[#FAF9F6] rounded-xl p-4 mb-3 text-center" data-testid="qris-display">
                <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Scan QRIS Berikut</div>
                <div className="inline-block bg-white p-3 rounded-2xl border border-[#E5E5E5]">
                  <img src={imageUrl(settings.qris_image)} alt="QRIS" className="w-64 h-64 object-contain" />
                </div>
                <a
                  href={imageUrl(settings.qris_image)}
                  download="qris-pembayaran"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="qris-download-customer"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-bold brand-text-primary hover:underline"
                >
                  <Upload size={14} className="rotate-180" /> Download QRIS
                </a>
                <div className="text-xs text-[#5C5C5C] mt-3 max-w-md mx-auto">
                  Silakan scan QRIS di atas menggunakan aplikasi e-wallet Anda sesuai <b>total pembayaran</b>,
                  lalu upload bukti pembayaran di bawah.
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-3 text-sm text-yellow-800">
                QRIS sedang tidak tersedia. Silakan hubungi admin via WhatsApp untuk pembayaran.
              </div>
            )
          ) : bankAccount && (
            <div className="brand-soft-bg rounded-xl p-3 mb-3">
              <div className="text-xs text-[#5C5C5C]">Transfer ke:</div>
              <div className="font-display font-bold text-lg">{bankAccount.bank}</div>
              <div className="font-mono text-base">{bankAccount.account_number}</div>
              <div className="text-sm text-[#5C5C5C]">a.n. {bankAccount.account_name}</div>
            </div>
          )}

          <div className="text-2xl font-display font-black brand-text-primary">{formatRp(order.total)}</div>
          <div className="text-xs text-[#5C5C5C] mt-1">
            {order.payment_method === "qris"
              ? "Bayar sesuai jumlah di atas, lalu upload bukti pembayaran."
              : "Transfer sesuai jumlah di atas, lalu upload bukti transfer."}
          </div>

          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Upload Bukti Pembayaran</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setProofFile(e.target.files?.[0])}
              className="block w-full text-sm mt-1"
              data-testid="proof-file-input"
            />
            <div className="text-[10px] text-[#5C5C5C] mt-1">JPG, JPEG, PNG, WEBP, PDF · Maks 2 MB</div>
            <Button onClick={uploadProof} disabled={!proofFile || uploading} className="mt-2 brand-btn-primary rounded-xl" data-testid="upload-proof-btn">
              <Upload size={14} className="mr-1" /> {uploading ? "Mengupload..." : "Upload Bukti Pembayaran"}
            </Button>
          </div>
          <Button variant="outline" onClick={cancelOrder} className="mt-3 rounded-xl brand-text-primary brand-border-primary" data-testid="cancel-order-btn">Batalkan Pesanan</Button>
            </>
          )}
        </section>
      )}

      {order.payment_proof_path && (
        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold">Bukti Pembayaran</h3>
            {order.payment_status === "verified" && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#2D6A4F]/15 text-[#2D6A4F]">Diverifikasi</span>
            )}
            {order.payment_status === "pending_verification" && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FFB703]/15 text-[#B5790F]">Menunggu Verifikasi</span>
            )}
            {order.payment_status === "rejected" && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-red-100 text-red-700">Ditolak</span>
            )}
          </div>
          <img src={imageUrl(order.payment_proof_path)} alt="Bukti" className="max-h-64 rounded-xl" />
        </section>
      )}

      {/* Items */}
      <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-4">
        <h3 className="font-display font-bold mb-3">Produk Pesanan</h3>
        <div className="space-y-3">
          {order.items.map((it, idx) => (
            <div key={`${it.product_id}-${idx}`} className="flex gap-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#FAF9F6]">
                {it.image && <img src={imageUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-2">{it.name}</div>
                <div className="text-xs text-[#5C5C5C]">{it.quantity} {it.unit} × {formatRp(it.unit_price)}</div>
              </div>
              <div className="font-bold text-sm">{formatRp(it.subtotal)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#E5E5E5] mt-4 pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#5C5C5C]">Subtotal</span><span>{formatRp(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#5C5C5C]">Ongkir ({order.shipping_method_name})</span><span>{formatRp(order.shipping_cost)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[#2D6A4F]"><span>Diskon</span><span>-{formatRp(order.discount)}</span></div>}
          <div className="flex justify-between text-base font-bold pt-2 border-t border-[#E5E5E5]"><span>Total</span><span className="brand-text-primary">{formatRp(order.total)}</span></div>
        </div>
      </section>

      <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-4">
        <h3 className="font-display font-bold mb-2">Alamat Pengiriman</h3>
        <div className="text-sm">
          <div className="font-bold">{order.address_snapshot?.recipient_name}</div>
          <div className="text-[#5C5C5C]">{order.address_snapshot?.phone}</div>
          <div className="text-[#5C5C5C]">{order.address_snapshot?.address}, {order.address_snapshot?.city} {order.address_snapshot?.postal_code}</div>
        </div>
        {(tracking?.awb || order.awb || order.tracking_number) && (
          <div className="mt-4 border-t border-[#E5E5E5] pt-4" data-testid="customer-tracking-section">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2">
              <Truck size={16} /> Tracking Pengiriman
            </h3>
            <TrackingPanel
              data={tracking || {
                awb: order.awb || order.tracking_number,
                tracking_url: order.tracking_url,
                courier_name: order.courier_name,
                courier_code: order.courier_code,
                courier_service: order.courier_service,
                tracking_status: order.tracking_status,
                tracking_events: order.tracking_events || [],
                tracking_last_sync: order.tracking_last_sync,
              }}
              role="customer"
              showCopyAwb={true}
            />
          </div>
        )}
        {order.notes && <div className="mt-3 text-sm text-[#5C5C5C]"><b>Catatan:</b> {order.notes}</div>}
      </section>

      {order.status === "shipped" && (
        <Button onClick={completeOrder} className="w-full h-12 bg-[#2D6A4F] hover:bg-[#1f4d3a] rounded-xl font-bold" data-testid="complete-order-btn">
          <CheckCircle2 size={16} className="mr-1" /> Tandai Pesanan Diterima
        </Button>
      )}

      {waPhone && (
        <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Halo, saya ingin tanya tentang pesanan ${order.order_number}`)}`} target="_blank" rel="noreferrer"
          className="mt-3 w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] text-white font-bold">
          <MessageCircle size={16} /> Tanya via WhatsApp
        </a>
      )}
    </div>
  );
}
